import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import SummaryApi from "../common";
import { authHeader } from "../helpers/auth-header";
import { toast } from "sonner";

// **Async thunk to fetch cart items**
export const fetchCart = createAsyncThunk(
  "cart/fetchCart",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(SummaryApi.addToCartProductCount.url, {
        headers: authHeader(),
      });


      if (data.listcarts) {
        return data.listcarts; // ✅ Ensure correct return
      }

      return { rows: [], count: 0, wallet: null, address: null }; // ✅ Prevent undefined issues
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("_bnac");
      }
      console.error("Failed to load cart.");
      return rejectWithValue(error.response?.data || "Error fetching cart.");
    }
  }
);

// **Async thunk for adding to cart (Now handles direct list update)**
export const addToCart = createAsyncThunk(
  "cart/addToCart",
  async ({ productId, variantId, showToast = true }, { rejectWithValue }) => {
    try {
      const payload = { productId, variantId };
      const { data } = await axios.post(SummaryApi.addToCartProduct.url, payload, {
        headers: authHeader(),
      });

      // Only show toast if explicitly requested (default is true for backward compatibility)
      if (showToast) {
        toast.success("Product Added to Cart!");
        // Trigger confetti celebration
        if (typeof window !== 'undefined') {
          import('../utils/confetti').then(({ celebrateCart }) => {
            celebrateCart();
          });
        }
      }

      if (data.listcarts) {
        return data.listcarts; // ✅ Correct return of the updated cart
      }

      return { rows: [], count: 0, wallet: null, address: null }; // ✅ Prevent undefined issues
    } catch (error) {
      console.error("Error adding to cart:", error);
      // toast.error("An error occurred while adding to the cart.");
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);
// updateCartQty
export const updateCartQty = createAsyncThunk(
  "cart/updateCartQty",
  async ({ cartPublicId, productId, variantId, quantity }, { rejectWithValue }) => {
    try {
      const payload = { productId, variantId, quantity };
      const { data } = await axios.put(
        `${SummaryApi.updateCartProduct.url}/${cartPublicId}`,
        payload,
        { headers: authHeader() }
      );

      return data.listcarts;
    } catch (error) {
      // For slab/cap violations (400/422), surface the server message so the
      // caller (Cart.js) can display a meaningful toast.
      const status = error.response?.status;
      const serverPayload = error.response?.data;
      if (status === 400 || status === 422) {
        return rejectWithValue(serverPayload || "Invalid quantity.");
      }
      toast.error("Failed to update cart.");
      return rejectWithValue(serverPayload || "Error updating cart.");
    }
  }
);

// deleteCartItem
export const deleteCartItem = createAsyncThunk(
  "cart/deleteCartItem",
  async (cartPublicId, { rejectWithValue }) => {
    try {
      const { data } = await axios.delete(
        `${SummaryApi.deleteCartProduct.url}/${cartPublicId}`,
        { headers: authHeader() }
      );

      return data.listcarts; // ✅ Return updated cart
    } catch (error) {
      toast.error("Failed to delete cart item.");
      return rejectWithValue(error.response?.data || "Error deleting cart.");
    }
  }
);

// **Cart slice definition**
const cartSlice = createSlice({
  name: "cart",
  initialState: {
    cartItems: [{ id: 0, name: "Placeholder" }], // Holds `rows` from API response
    cartCount: 0, // Holds `count`
    wallet: null, // Holds `wallet` info
    address: null, // Holds `address`
    minInstantDeliveryFee: null,
    /** From GET /cart/list listcarts: "business" | "retail" */
    customerSegment: null,
    /** Business user: any line exceeds per-order max */
    b2bPerOrderBlocked: false,
    /** Checkout-style violations from cart list */
    b2bLineIssues: [],
    status: "idle",
    error: null,
  },
  reducers: {
    clearCart: (state) => {
      state.cartItems = [];
      state.cartCount = 0;
      state.wallet = null;
      state.address = null;
      state.minInstantDeliveryFee = null;
      state.customerSegment = null;
      state.b2bPerOrderBlocked = false;
      state.b2bLineIssues = [];
    },
    walletUpdate: (state, action) => {
      state.wallet.balance = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCart.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchCart.fulfilled, (state, action) => {

        state.status = "succeeded";

        if (action.payload) {
          state.cartItems = action.payload.rows || [];
          state.cartCount = action.payload.count || 0;
          state.wallet = action.payload.wallet || null;
          state.address = action.payload.address || null;
          state.minInstantDeliveryFee = action.payload.minInstantDeliveryFee || null;
          state.customerSegment = action.payload.customerSegment ?? null;
          state.b2bPerOrderBlocked = Boolean(action.payload.b2bPerOrderBlocked);
          state.b2bLineIssues = Array.isArray(action.payload.b2bLineIssues)
            ? action.payload.b2bLineIssues
            : [];
        } else {
          console.warn("❌ Redux State Update Skipped: action.payload is undefined.");
        }
      })
      .addCase(fetchCart.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(addToCart.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addToCart.fulfilled, (state, action) => {

        state.status = "succeeded";

        if (action.payload) {
          state.cartItems = action.payload.rows || []; // ✅ Ensure rows exist
          state.cartCount = action.payload.count || 0; // ✅ Ensure count exists
          state.wallet = action.payload.wallet || null; // ✅ Ensure wallet exists
          state.address = action.payload.address || null; // ✅ Ensure address exists
          state.customerSegment = action.payload.customerSegment ?? null;
          state.b2bPerOrderBlocked = Boolean(action.payload.b2bPerOrderBlocked);
          state.b2bLineIssues = Array.isArray(action.payload.b2bLineIssues)
            ? action.payload.b2bLineIssues
            : [];
        } else {
          console.warn("Redux State Update Skipped: action.payload is undefined.");
        }
      })
      .addCase(addToCart.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
    builder
      .addCase(updateCartQty.fulfilled, (state, action) => {
        state.status = "succeeded";

        if (action.payload) {
          state.cartItems = action.payload.rows || [];
          state.cartCount = action.payload.count || 0;
          state.wallet = action.payload.wallet || null;
          state.address = action.payload.address || null;
          state.minInstantDeliveryFee =
            action.payload.minInstantDeliveryFee ?? state.minInstantDeliveryFee;
          state.customerSegment = action.payload.customerSegment ?? null;
          state.b2bPerOrderBlocked = Boolean(action.payload.b2bPerOrderBlocked);
          state.b2bLineIssues = Array.isArray(action.payload.b2bLineIssues)
            ? action.payload.b2bLineIssues
            : [];
        }
      })
      .addCase(deleteCartItem.fulfilled, (state, action) => {
        state.status = "succeeded";

        if (action.payload) {
          state.cartItems = action.payload.rows || [];
          state.cartCount = action.payload.count || 0;
          state.wallet = action.payload.wallet || null;
          state.address = action.payload.address || null;
          state.minInstantDeliveryFee =
            action.payload.minInstantDeliveryFee ?? state.minInstantDeliveryFee;
          state.customerSegment = action.payload.customerSegment ?? null;
          state.b2bPerOrderBlocked = Boolean(action.payload.b2bPerOrderBlocked);
          state.b2bLineIssues = Array.isArray(action.payload.b2bLineIssues)
            ? action.payload.b2bLineIssues
            : [];
        }
      });

  },
});

export const { clearCart, walletUpdate } = cartSlice.actions;
export default cartSlice.reducer;
